/**
 * Copyright © Veeam Software Group GmbH.
 */

import React from 'react';
import {
    Form,
    FormValidator,
    FormLayout,
    isValid,
    useExternalFormApi,
    NoLabelGroup,
    LinkButton,
    Separator,
    FormTitle,
} from '@veeam-vspc/components';
import { usePageWizardStore } from '@veeam-vspc/components/src/PageWizard/hooks/use-page-wizard-store';
import { capitalize, formatStr } from '@veeam-vspc/core';

import type { WizardStep, WizardStepData } from '@veeam-vspc/components';

import { StepLayout } from 'components/layouts/StepLayout';
import { useLang } from 'views/providers/LangProvider/hooks';
import settingsIcon from 'images/actions/settings.svg';
import { PerMonthField } from 'components/controls/PerMonthField';
import { PerBytesField } from 'components/controls/PerBytesField';
import { FormNote } from 'components/controls/FormNote';
import { ManagedServicesAdvanced } from './components/ManagedServicesAdvanced';

import type { SubscriptionPlanModel } from 'core/interfaces';

export const stepValidate = (data: SubscriptionPlanModel) => {
    const validator = new FormValidator(data);

    validator
        .validate('managedWorkstationPrice')
        .number()
        .min(0)
        .max(1000000);
    validator
        .validate('managedServerPrice')
        .number()
        .min(0)
        .max(1000000);

    validator
        .validate('managedVmPrice')
        .number()
        .min(0)
        .max(1000000);
    validator
        .validate('managedCdpVmPrice')
        .number()
        .min(0)
        .max(1000000);

    validator
        .validate('sourceAmountOfDataPrice')
        .number()
        .min(0)
        .max(1000000);
    validator
        .validate('nasShortTermUsedSpacePrice')
        .number()
        .min(0)
        .max(1000000);
    validator
        .validate('nasLongTermUsedSpacePrice')
        .number()
        .min(0)
        .max(1000000);

    return validator.result();
};

export const getManagedServicesStep = (title: string): WizardStep<SubscriptionPlanModel> => ({
    title,
    validate: ({ data, stepData, isEdit }) => isValid(stepValidate, data),
    render: data => <ManagedServicesStep {...data} />,
});

const ManagedServicesStep = (props: WizardStepData<SubscriptionPlanModel>) => {
    const lang = useLang();
    const { data, validationState, onDataChange } = props;
    const formApi = useExternalFormApi();
    const wizardContext = usePageWizardStore();
    const showPanel = () => wizardContext.openPanel(hide => <ManagedServicesAdvanced {...props} hide={hide} />);
    const currencyPerAgent = formatStr(lang.CURRENCY_PER_AGENT, data.currency);
    const currencyPerVm = formatStr(lang.CURRENCY_PER_VM, data.currency);
    const currencyPer = formatStr(lang.CURRENCY_PER, data.currency);

    return (
        <StepLayout
            title={capitalize(lang.MANAGED_BACKUP)}
            description={lang.DEFINE_SUBSCRIPTION_PLAN_PRICE_FOR_MANAGED_BACKUP}
        >
            <Form
                value={data}
                validate={stepValidate}
                validationState={validationState}
                externalFormApi={formApi}
                onChange={onDataChange}
            >
                <FormLayout inlineLabel>
                    <FormTitle>{lang.BACKUP_AGENTS_PER_MONTH}</FormTitle>

                    <PerMonthField
                        name='managedWorkstationPrice'
                        label={capitalize(lang.WORKSTATION_AGENT, true)}
                        suffix={currencyPerAgent}
                    />

                    <PerMonthField
                        name='managedServerPrice'
                        label={capitalize(lang.SERVER_AGENT, true)}
                        suffix={currencyPerAgent}
                    />

                    <FormTitle>{lang.BACKUP_SERVERS_PER_MONTH}</FormTitle>

                    <PerMonthField
                        name='managedVmPrice'
                        label={lang.MANAGED_VM}
                        suffix={currencyPerVm}
                    />

                    <PerMonthField
                        name='managedCdpVmPrice'
                        label={lang.VM_WITH_CDP_ENABLED}
                        suffix={currencyPerVm}
                    />

                    <PerBytesField
                        label={`${lang.FILE_SHARE_SIZE}:`}
                        valueName={'sourceAmountOfDataPrice'}
                        unitsName={'sourceAmountOfDataMultiplier'}
                        suffix={currencyPer}
                    />

                    <PerBytesField
                        label={`${lang.FILE_SHARE_BACKUP_SIZE}:`}
                        valueName={'nasShortTermUsedSpacePrice'}
                        unitsName={'nasShortTermUsedSpaceMultiplier'}
                        suffix={currencyPer}
                    />

                    <PerBytesField
                        label={`${lang.FILE_SHARE_ARCHIVE_SIZE}:`}
                        valueName={'nasLongTermUsedSpacePrice'}
                        unitsName={'nasLongTermUsedSpaceMultiplier'}
                        suffix={currencyPer}
                    />

                    <Separator />

                    <NoLabelGroup
                        content={(
                            <div>
                                <LinkButton
                                    onClick={showPanel}
                                    iconBefore={settingsIcon}
                                >
                                    {`${lang.ADVANCED_SETTINGS}...`}
                                </LinkButton>

                                <FormNote>{lang.IN_THE_ADVANCED_SETTINGS_YOU_CAN_CONFIGURE_THE_NUMBER_OF_WORKLOADS_AND}</FormNote>
                            </div>
                        )}
                    />
                </FormLayout>
            </Form>
        </StepLayout>
    );
};
